<template>
  <div style="width: 100%; height: 300px;">
    <v-chart
      ref="chartRef"
      class="chart-container"
      :option="chartOptions"
      autoresize
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import VChart from 'vue-echarts'
import { markLineFor } from '@/zones/zoneMarkers'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'
import { use } from 'echarts/core'
import { MarkLineComponent } from 'echarts/components'

use([MarkLineComponent])

// Props
const props = defineProps({
  chartData: Object, // expected shape: { "2025-06-01": { "Entrance 1": {...} }, ... }
  zoneName: String,
  timeSpan: String,
  // "Something changed at this spot" days (GET /zone_markers/): a dashed line each.
  markers: { type: Array, default: () => [] },
})

const chartRef = ref(null)

const { t } = useI18n()
// Dutch numbers (1.234 · 12,5) for tooltips and the value axis; English keeps
// ECharts' own formatting, so these are only set when the portal isn't English.
const nlNum = v => formatNumber(v, { decimals: Math.min(2, (String(v).split('.')[1] || '').length) })

// Chart Options
const chartOptions = computed(() => {
  const dates = []
  const durations  = []

  if (props.chartData && props.zoneName) {
    for (const [date, zoneData] of Object.entries(props.chartData)) {
      if (zoneData[props.zoneName]) {
        dates.push(date)
        durations.push(zoneData[props.zoneName].average_duration ?? 0)
      }
    }
  }

  const max = Math.max(...durations, 10)

  return {
    tooltip: currentLanguage() === 'en' ? { trigger: 'axis' } : { trigger: 'axis', valueFormatter: nlNum },
    visualMap: [
      {
        show: false,
        type: 'continuous',
        seriesIndex: 0,
        min: 0,
        max: max,
        inRange: {
          color: ['#42546160', '#425461']
        }
      }
    ],
    grid: {
      containLabel: true,
      bottom: 0
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { rotate: 45 }
    },
    yAxis: {
      type: 'value',
      name: t('charts.line.avgDurationAxis', { span: props.timeSpan }),
      nameTextStyle: { align: 'left'},
      ...(currentLanguage() === 'en' ? {} : { axisLabel: { formatter: nlNum } })
    },
    series: [
      {
        name: t('charts.line.avgDuration'),
        data: durations,
        markLine: markLineFor(props.markers, dates),
        type: 'line',
        smooth: true,
        lineStyle: {
          width: 3
        }
      }
    ]
  }
})

// Resize on mount
onMounted(() => {
  setTimeout(() => {
    chartRef.value?.resize()
  }, 0)
})
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
}
</style>